import { PropertyDetailsAppeal } from "utils/types";
import { ActiveSubscription, PropertyDetails } from "utils/types";

export const PROPERTY_SURVEY_QUESTION_STATES = [
	"ONBOARDING_INTRO",
	"INTRO",
	"NOTICE_OF_VALUE",
	"NOTICE_OF_VALUE_UPLOAD",
	"PRIMARY_RESIDENCE",
	"CURRENT_HOMESTEAD_STATUS",
	"TAKEOVER",
	"HEARING_NOTICE",
	"HEARING_NOTICE_UPLOAD",
	"EVIDENCE_INTRO",
	"PURCHASED_RECENTLY",
	"ARMS_LENGTH_TRANSACTION",
	"ARMS_LENGTH_TRANSACTION_DESCRIPTION",
	"INCOME_PRODUCING",
	"DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
	"EASEMENT",
	"FLOODING",
	"FOUNDATION_ISSUES",
	"ROOF_DAMAGE",
	"MAJOR_DAMAGE",
	"CATCH_ALL",
	"CONCIERGE_OPTIONS",
	"CONCIERGE_CONFIRMATION",
	"INTERNET_REDUCTION",
	"INSURANCE_MONITORING",
	"MORTGAGE_AND_EQUITY",
	//"HOW_DID_YOU_HEAR_ABOUT_US",
	"WILDCARD",
	"CONFIRMATION",
	"CONCIERGE_PAYMENT",
] as const;

export type PropertySurveyQuestionState =
	(typeof PROPERTY_SURVEY_QUESTION_STATES)[number];

export const valueIsPropertySurveyQuestionState = (
	v: any
): v is PropertySurveyQuestionState => {
	try {
		return PROPERTY_SURVEY_QUESTION_STATES.includes(
			v as PropertySurveyQuestionState
		);
	} catch {
		return false;
	}
};

export const getQuestionId = (state: PropertySurveyQuestionState) => {
	const PROPERTY_SURVEY_QUESTION_ID_MAP: {
		[key in PropertySurveyQuestionState]: string;
	} = {
		ONBOARDING_INTRO: "onboardingIntro",
		INTRO: "intro",
		NOTICE_OF_VALUE: "noticeOfValue",
		NOTICE_OF_VALUE_UPLOAD: "noticeOfValue",
		PRIMARY_RESIDENCE: "primaryResidence",
		CURRENT_HOMESTEAD_STATUS: "currentHomesteadStatus",
		TAKEOVER: "takeover",
		HEARING_NOTICE: "hearingNotice",
		HEARING_NOTICE_UPLOAD: "hearingNotice",
		EVIDENCE_INTRO: "evidenceIntro",
		PURCHASED_RECENTLY: "purchasedRecently",
		ARMS_LENGTH_TRANSACTION: "armsLengthTransaction",
		ARMS_LENGTH_TRANSACTION_DESCRIPTION: "armsLengthTransactionDescription",
		INCOME_PRODUCING: "incomeProducing",
		DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO: "damageAndDeferredMaintenanceIntro",
		EASEMENT: "easement",
		FLOODING: "flooding",
		FOUNDATION_ISSUES: "foundationIssues",
		ROOF_DAMAGE: "roofDamage",
		MAJOR_DAMAGE: "majorDamage",
		CATCH_ALL: "catchAll",
		CONCIERGE_OPTIONS: "conciergeOptions",
		CONCIERGE_CONFIRMATION: "conciergeConfirmation",
		INTERNET_REDUCTION: "internetReduction",
		INSURANCE_MONITORING: "insuranceMonitoring",
		MORTGAGE_AND_EQUITY: "mortgageAndEquity",
		//HOW_DID_YOU_HEAR_ABOUT_US: "heardAbout",
		WILDCARD: "wildcard",
		CONFIRMATION: "confirmation",
		CONCIERGE_PAYMENT: "conciergePayment",
	};
	return PROPERTY_SURVEY_QUESTION_ID_MAP[state];
};

export interface PropertySurveyQuestionsAnswers
	extends Pick<
			PropertyDetailsAppeal,
			| "files"
			| "q_profile_seen_intro"
			| "q_notice_received"
			| "q_primary_residence"
			| "q_current_homestead_status"
			| "is_takeover"
			| "question_deferred"
			| "question_purchased"
			| "question_income"
			| "last_sale_date_prop"
			| "last_sale_price_prop"
			| "question_armslength"
			| "question_armslength_desc"
			//| "question_heardabout"
			| "q_negative_impact"
			| "q_negative_impact_list"
			| "q_negative_impact_other"
			| "q_negative_impact_cost"
			| "q_negative_impact_date"
			| "q_flooded"
			| "q_flooded_cause"
			| "q_flooded_desc"
			| "q_flooded_cost"
			| "q_flooded_date"
			| "q_structural_issues"
			| "q_structural_issues_list"
			| "q_structural_issues_desc"
			| "q_structural_issues_cost"
			| "q_structural_issues_date"
			| "q_major_damage"
			| "q_major_damage_list"
			| "q_major_damage_desc"
			| "q_major_damage_cost"
			| "q_major_damage_date"
			| "q_roof_damage"
			| "q_roof_damage_desc"
			| "q_roof_damage_date"
			| "q_roof_damage_cost"
			| "q_catchall"
			| "q_catchall_desc"
			| "q_catchall_cost"
			| "q_catchall_date"
			| "q_wildcard_type"
			| "q_wildcard_answer"
			| "q_yardsign"
			| "q_hearing_notice"
			| "q_hearing_notice_date"
			| "customer_completed_survey"
		>,
		Pick<PropertyDetails, "state" | "county" | "auth_company_type"> {
	// Pick<UserData, "has_answered_heardabout">
	enterprise: boolean;
	direction: "forwards" | "backwards";
}
export interface PropertySurveyQuestionFlowContext
	extends PropertySurveyQuestionsAnswers {
	progress: number | undefined;
	in_season: boolean;
	account_tier: string | null;
	active_subscription: ActiveSubscription[];
	current_account_tier: string | null;
	show_internet_reduction_question: boolean;
	show_insurance_monitoring_question: boolean;
	show_mortgage_and_equity_question: boolean;
}

export type PropertySurveyQuestionFlowEvent =
	| { type: "back" }
	| { type: "next" }
	| { type: "" }
	| { type: "init" }
	| { type: "restart" }
	| {
			type: "update_context";
			context: Partial<PropertySurveyQuestionFlowContext>;
	  };
